import React, { FC, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as CopyIcon } from '@blink/assets/src/icons/copy.svg';
import { ButtonStroke } from '@blink/components/src/Button/ButtonStroke';
import { ButtonSize } from '@blink/components/src/Button/Button.types';
import { TextButton } from '@blink/components';
import { WalletHelper } from '@blink/components/src/utils';
import { useDispatch } from 'react-redux';
import { ReactComponent as Etherscan } from '@blink/assets/src/icons/etherscan.svg';
import { ReactComponent as Loader } from '@blink/assets/src/icons/loaderDash.svg';

import { usePoolDetailsStyle } from './Pool.style';
import { useAgreementData } from './useAgreementData';

import { getAgreementsAction } from '@/shared/store/agreements/actions';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { ROLE } from '@/shared/api/borrow';
import { PoolDetailsTabs } from '@/widgets/PoolDetailsTabs';
import {
    AssetIcon,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
} from '@/shared/ui';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';

export const enum ContentListType {
    DESCRIPTION = 'description',
    ALLOWED = 'allowed',
}

export const Pool: FC = () => {
    const classes = usePoolDetailsStyle();
    const { address } = useParams();
    const [searchParams] = useSearchParams();
    const role = searchParams.get('role');

    const dispatch = useDispatch();

    const { tokens, agreements, type } = useAgreementData(role!);

    useEffect(() => {
        dispatch(
            getAgreementsAction({
                role: role as ROLE,
            }),
        );
    }, [type, role]);

    const currentAgreement = useMemo(
        () =>
            agreements.find((agreement) => {
                return agreement.address === address && agreement.role === role;
            }),
        [agreements],
    );

    const handleCopyAddress = useCopyToClipboard(address || '');

    const refreshAgreements = async () =>
        dispatch(
            getAgreementsAction({
                role: role as ROLE,
                withoutCache: true,
            }),
        );

    const handleBtnClick = () => {
        if (currentAgreement?.role === ROLE.LENDER) {
            dispatch(
                showModalAction({
                    modalName: ModalName.POOLS_SUPPLY_MODAL,
                    modalTitle: 'Supply Liquidity',
                    modalProps: {
                        tokenSymbol: currentAgreement.leverage.symbol,
                        balance: currentAgreement.leverage.userBalance,
                        currentApy: currentAgreement.apy,
                        decimals: currentAgreement.leverage.decimals,
                        totalDeposited: currentAgreement.totalDeposited,
                        totalDepositThreshold: currentAgreement.totalDepositThreshold,
                        poolUtilization: currentAgreement.poolUtilization,
                        agreementAddress: currentAgreement.address,
                        tokenAddress: currentAgreement.leverage.address,
                        refresh: refreshAgreements,
                    },
                }),
            );

            return;
        } else if (currentAgreement?.role === ROLE.BORROWER) {
            const leverage = tokens.find((item) => item.id === currentAgreement.leverageTokenId);

            dispatch(
                showModalAction({
                    modalName: ModalName.BORROW_MODAL,
                    modalTitle: `Borrow ${leverage?.symbol}`,
                    modalProps: {
                        tokenSymbol: leverage?.symbol,
                        agreementAddress: address,
                        tokenAddress: leverage?.address,
                    },
                }),
            );

            return;
        }
    };

    const handleEtherscan = () => {
        window.open(`https://etherscan.io/address/${address}`, '_blank');
    };

    const handleBack = () => {
        window.history.back();
    };

    return (
        <>
            {!type || !currentAgreement || (currentAgreement && currentAgreement.address === '') ? (
                <div className={classes.loader}>
                    <Loader />
                </div>
            ) : (
                <div>
                    <div className={classes.top}>
                        <div className={classes.breadcrumbs}>
                            <Breadcrumb>
                                <BreadcrumbList>
                                    <BreadcrumbItem>
                                        <BreadcrumbLink onClick={handleBack}>Pools</BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbSeparator />
                                    <BreadcrumbItem>
                                        <BreadcrumbLink>
                                            <div
                                                className={classes.addressWrapper}
                                                data-testid='pool-address'
                                            >
                                                <div>
                                                    {currentAgreement.title}{' '}
                                                    <span>{`(${WalletHelper.addressShortName(
                                                        currentAgreement.address,
                                                    )})`}</span>
                                                </div>
                                                <TextButton
                                                    onClick={handleCopyAddress}
                                                    data-testid='pool-copy-address-button'
                                                    className={classes.textButton}
                                                >
                                                    <CopyIcon />
                                                </TextButton>
                                            </div>
                                        </BreadcrumbLink>
                                    </BreadcrumbItem>
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>
                        <div className={classes.heading}>
                            <div className={classes.content}>
                                <AssetIcon
                                    data-testid={`pool-icon-${currentAgreement.leverage.symbol}`}
                                    assetName={currentAgreement.leverage.symbol}
                                    className='h-[40px] w-[40px]'
                                />
                                <div className={classes.title}>
                                    <div className={classes.titleText} data-testid='pool-title'>
                                        {currentAgreement.title}
                                    </div>
                                </div>
                                <div
                                    className={classes.etherscan}
                                    onClick={handleEtherscan}
                                    data-testid='etherscan'
                                >
                                    <Etherscan />
                                </div>
                            </div>
                            <div className={classes.buttonWrapper}>
                                <ButtonStroke
                                    isPrimary
                                    size={ButtonSize.md}
                                    className={classes.borrowButton}
                                    disabled={false}
                                    data-testid={'pool-supply-button'}
                                    onClick={handleBtnClick}
                                >
                                    {currentAgreement.role === ROLE.LENDER ? 'Supply' : 'Borrow'}
                                </ButtonStroke>
                            </div>
                        </div>
                    </div>

                    <div className={classes.tabs}>
                        <PoolDetailsTabs />
                    </div>
                </div>
            )}
        </>
    );
};
